/**
 * Utilities for sharing bet slip summaries.
 */

import type { SavedBet } from '../context/BetContext'

/**
 * Group bets by fixture name
 */
function groupBetsByFixture(bets: SavedBet[]): Record<string, SavedBet[]> {
  return bets.reduce((acc, bet) => {
    if (!acc[bet.fixture]) {
      acc[bet.fixture] = []
    }
    acc[bet.fixture].push(bet)
    return acc
  }, {} as Record<string, SavedBet[]>)
}

/**
 * Get confidence emoji
 */
function getConfidenceEmoji(confidence: string): string {
  switch (confidence) {
    case 'high':
      return '🟢'
    case 'med':
      return '🟡'
    default:
      return '🔴'
  }
}

/**
 * Format a single bet line
 */
function formatBet(bet: SavedBet): string {
  const conf = getConfidenceEmoji(bet.confidence)
  const lineStr = bet.line ? ` ${bet.isOver ? 'O' : 'U'}${bet.line}` : ''
  return `  ${conf} ${bet.title}${lineStr} (${bet.confidencePct}%)`
}

/**
 * Format the entire bet slip as plain text for sharing
 */
export function formatBetSlip(bets: SavedBet[]): string {
  if (bets.length === 0) {
    return 'No selections in bet slip.'
  }

  const grouped = groupBetsByFixture(bets)
  const lines: string[] = []

  lines.push('📊 StatScout Bet Builder')
  lines.push('═'.repeat(30))
  lines.push('')

  for (const [fixture, fixtureBets] of Object.entries(grouped)) {
    lines.push(`⚽ ${fixture}`)
    for (const bet of fixtureBets) {
      lines.push(formatBet(bet))
    }
    lines.push('')
  }

  lines.push('─'.repeat(30))
  lines.push(`📋 Total Selections: ${bets.length}`)

  // Calculate average confidence
  const avgConfPct = Math.round(
    bets.reduce((sum, b) => sum + b.confidencePct, 0) / bets.length
  )
  lines.push(`📈 Avg Confidence: ${avgConfPct}%`)

  lines.push('')
  lines.push('Built with statscout.io')

  return lines.join('\n')
}

/**
 * Copy text to clipboard
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    // Fallback for older browsers
    try {
      const textArea = document.createElement('textarea')
      textArea.value = text
      textArea.style.position = 'fixed'
      textArea.style.left = '-9999px'
      document.body.appendChild(textArea)
      textArea.focus()
      textArea.select()
      const result = document.execCommand('copy')
      document.body.removeChild(textArea)
      return result
    } catch {
      return false
    }
  }
}

/**
 * Share via Web Share API if available
 */
export function shareViaWebShare(text: string): boolean {
  if (navigator.share) {
    navigator.share({ text }).catch(() => {
      // User cancelled or share failed - ignore
    })
    return true
  }
  return false
}
